<!--
 * @Author: Tahiti
 * @Date: 2025-12-04 16:22:39
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-04 17:23:56
 * @Description: 下拉菜单组件 - 支持自定义触发按钮和菜单内容，支持 click 和 hover 两种触发方式
 * @FilePath: /weryai-web/components/ui/PopMenu.vue
-->
<template>
    <div ref="containerRef" class="relative" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
        <!-- 触发按钮插槽 -->
        <div :class="['inline-block', triggerClass]" @click.stop="handleClick" @mouseenter="handleTriggerMouseEnter"
            @mouseleave="handleTriggerMouseLeave">
            <slot name="trigger">
                <!-- 默认触发按钮 -->
                <button type="button" class="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">
                    Menu
                </button>
            </slot>
        </div>

        <!-- hover 模式下的桥接区域，连接触发按钮和菜单 -->
        <div v-if="props.trigger === 'hover' && isDropdownOpen" ref="bridgeRef"
            :class="['absolute z-[9998]', positionClasses]" :style="bridgeStyle" @mouseenter="handleBridgeMouseEnter">
        </div>

        <!-- 下拉菜单内容 -->
        <Transition enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 transform -translate-y-2 scale-95"
            enter-to-class="opacity-100 transform translate-y-0 scale-100"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 transform translate-y-0 scale-100"
            leave-to-class="opacity-0 transform -translate-y-2 scale-95">
            <div v-if="isDropdownOpen" ref="menuRef" :class="[
                'absolute pop-menu z-[9999] bg-[#1c1c20] rounded-lg shadow-lg ',
                positionClasses,
                widthClass,
            ]" :style="menuStyle" @click.stop @mouseenter="handleMenuMouseEnter" @mouseleave="handleMenuMouseLeave">
                <!-- 菜单内容插槽 -->
                <slot name="content">
                    <!-- 默认菜单内容 -->
                    <div class="p-2 text-white text-sm">菜单内容</div>
                </slot>
            </div>

        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

/**
 * Props 定义
 */
interface Props {
    /** 控制菜单显示/隐藏（支持 v-model） */
    modelValue?: boolean
    /** 触发方式：click | hover */
    trigger?: 'click' | 'hover'
    /** 菜单位置：left | right | center | side-right | side-right-bottom */
    position?: 'left' | 'right' | 'center' | 'side-right' | 'side-right-bottom'
    /** 菜单宽度：auto | min-w-36 | min-w-48 | min-w-64 | 自定义类名 */
    width?: 'auto' | 'min-w-36' | 'min-w-48' | 'min-w-64' | string
    /** 菜单距离触发按钮的间距（单位：px） */
    gap?: number
    /** 是否点击外部自动关闭（仅 click 模式有效） */
    closeOnClickOutside?: boolean
    /** hover 模式下的延迟关闭时间（单位：ms） */
    hoverDelay?: number
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    trigger: 'click',
    position: 'right',
    width: 'min-w-36',
    gap: 2,
    closeOnClickOutside: true,
    hoverDelay: 200,
})

/**
 * Emits 定义
 */
const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    open: []
    close: []
}>()

/**
 * 响应式状态
 */
const containerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const bridgeRef = ref<HTMLElement | null>(null)
const isDropdownOpen = ref(props.modelValue)
const isMouseInMenu = ref(false) // 跟踪鼠标是否在菜单区域内
let hoverTimer: ReturnType<typeof setTimeout> | null = null

/**
 * 计算属性：菜单位置类名
 */
const positionClasses = computed(() => {
    const classes: Record<string, string> = {
        left: 'left-0 top-full',
        right: 'right-0 top-full',
        center: 'left-1/2 -translate-x-1/2 top-full',
        'side-right': 'left-full top-0',
        'side-right-bottom': 'left-full bottom-0',
    }
    return classes[props.position] || classes.right
})

/**
 * 计算属性：菜单宽度类名
 */
const widthClass = computed(() => {
    // 如果是预设的宽度值，直接返回
    if (['auto', 'min-w-36', 'min-w-48', 'min-w-64'].includes(props.width)) {
        return props.width === 'auto' ? '' : props.width
    }
    // 否则返回自定义类名
    return props.width
})

/**
 * 计算属性：菜单样式
 */
const menuStyle = computed(() => {
    if (props.position === 'side-right' || props.position === 'side-right-bottom') {
        return {
            marginLeft: `${props.gap}px`,
        }
    }
    return {
        marginTop: `${props.gap}px`,
    }
})

/**
 * 计算属性：桥接区域样式（hover 模式下使用）
 */
const bridgeStyle = computed(() => {
    if (props.trigger !== 'hover') {
        return {}
    }
    if (props.position === 'side-right' || props.position === 'side-right-bottom') {
        return {
            left: '100%',
            top: '0',
            width: `${props.gap}px`,
            height: '100%',
            pointerEvents: 'auto' as const,
        }
    }
    return {
        top: '100%',
        height: `${props.gap}px`,
        width: '100%',
        pointerEvents: 'auto' as const,
    }
})

/**
 * 计算属性：触发区域类名
 */
const triggerClass = computed(() => {
    return props.trigger === 'hover' ? 'cursor-pointer' : ''
})

/**
 * 清除 hover 延迟定时器
 */
const clearHoverTimer = () => {
    if (hoverTimer) {
        clearTimeout(hoverTimer)
        hoverTimer = null
    }
}

/**
 * 处理点击事件
 */
const handleClick = () => {
    if (props.trigger === 'click') {
        toggleDropdown()
    }
}

/**
 * 处理触发按钮鼠标进入（hover 模式）
 */
const handleTriggerMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
        openDropdown()
    }
}

/**
 * 处理触发按钮鼠标离开（hover 模式）
 */
const handleTriggerMouseLeave = () => {
    if (props.trigger === 'hover') {
        // 如果鼠标已经在菜单上，不关闭
        if (isMouseInMenu.value) {
            return
        }
        // 延迟关闭，给用户时间移动到菜单
        hoverTimer = setTimeout(() => {
            if (!isMouseInMenu.value) {
                closeDropdown()
            }
        }, props.hoverDelay)
    }
}

/**
 * 处理桥接区域鼠标进入（hover 模式）
 */
const handleBridgeMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
    }
}

/**
 * 处理菜单鼠标进入（hover 模式）
 */
const handleMenuMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
        isMouseInMenu.value = true
    }
}

/**
 * 处理菜单鼠标离开（hover 模式）
 */
const handleMenuMouseLeave = () => {
    if (props.trigger === 'hover') {
        isMouseInMenu.value = false
        hoverTimer = setTimeout(() => {
            closeDropdown()
        }, props.hoverDelay)
    }
}

/**
 * 处理容器鼠标进入（hover 模式）
 */
const handleMouseEnter = () => {
    if (props.trigger === 'hover') {
        clearHoverTimer()
    }
}

/**
 * 处理容器鼠标离开（hover 模式）
 */
const handleMouseLeave = () => {
    if (props.trigger === 'hover') {
        // 如果鼠标在菜单上，不关闭
        if (isMouseInMenu.value) {
            return
        }
        hoverTimer = setTimeout(() => {
            if (!isMouseInMenu.value) {
                closeDropdown()
            }
        }, props.hoverDelay)
    }
}

/**
 * 切换下拉菜单显示状态
 */
const toggleDropdown = () => {
    if (isDropdownOpen.value) {
        closeDropdown()
    } else {
        openDropdown()
    }
}

/**
 * 打开下拉菜单
 */
const openDropdown = () => {
    isDropdownOpen.value = true
    updateModelValue()
    emit('open')
}

/**
 * 关闭下拉菜单
 */
const closeDropdown = () => {
    isDropdownOpen.value = false
    isMouseInMenu.value = false // 重置菜单内状态
    updateModelValue()
    emit('close')
}

/**
 * 更新 v-model 值
 */
const updateModelValue = () => {
    emit('update:modelValue', isDropdownOpen.value)
}

/**
 * 点击外部区域关闭菜单
 */
const handleClickOutside = (event: MouseEvent) => {
    if (!props.closeOnClickOutside || !isDropdownOpen.value) {
        return
    }

    const target = event.target as HTMLElement
    if (
        containerRef.value &&
        !containerRef.value.contains(target) &&
        menuRef.value &&
        !menuRef.value.contains(target)
    ) {
        closeDropdown()
    }
}

/**
 * 监听 props.modelValue 变化
 */
watch(
    () => props.modelValue,
    newValue => {
        isDropdownOpen.value = newValue
    }
)

/**
 * 生命周期：挂载时添加点击外部监听
 */
onMounted(() => {
    if (props.closeOnClickOutside && props.trigger === 'click') {
        document.addEventListener('click', handleClickOutside)
    }
})

/**
 * 生命周期：卸载时移除点击外部监听和清理定时器
 */
onUnmounted(() => {
    if (props.closeOnClickOutside && props.trigger === 'click') {
        document.removeEventListener('click', handleClickOutside)
    }
    clearHoverTimer()
})

/**
 * 暴露方法供外部调用
 */
defineExpose({
    open: openDropdown,
    close: closeDropdown,
    toggle: toggleDropdown,
    isOpen: () => isDropdownOpen.value,
})
</script>

<style scoped lang="less">
.pop-menu::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(180deg,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 100%);
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: 1;
    pointer-events: none;
    /* 确保伪元素不拦截鼠标事件 */
}
</style>
